import { Temporal } from "@js-temporal/polyfill";
import { useWeather } from "@/app/lib/data-context";
import type { WeatherCondition } from "@/types";

const PAPER = "oklch(0.99 0.006 90)";

// Day gradients — each condition picks a faithful hue and carries it through
// the upper two stops with enough chroma to read. All gradients terminate at
// the cream PAPER end stop so the banner fades cleanly into the page below.
const COND_BG: Record<string, string> = {
  sun: `linear-gradient(180deg, oklch(0.87 0.16 95) 0%, oklch(0.93 0.1 90) 55%, ${PAPER} 100%)`,
  heat: `linear-gradient(180deg, oklch(0.83 0.13 50) 0%, oklch(0.92 0.085 65) 55%, ${PAPER} 100%)`,
  cloud: `linear-gradient(180deg, oklch(0.84 0.028 240) 0%, oklch(0.93 0.015 235) 55%, ${PAPER} 100%)`,
  rain: `linear-gradient(180deg, oklch(0.76 0.05 230) 0%, oklch(0.88 0.028 225) 55%, ${PAPER} 100%)`,
  storm: `linear-gradient(180deg, oklch(0.62 0.06 250) 0%, oklch(0.82 0.035 240) 55%, ${PAPER} 100%)`,
  wind: `linear-gradient(180deg, oklch(0.88 0.035 165) 0%, oklch(0.94 0.02 155) 55%, ${PAPER} 100%)`,
  frost: `linear-gradient(180deg, oklch(0.9 0.04 205) 0%, oklch(0.95 0.022 200) 55%, ${PAPER} 100%)`,
  cold: `linear-gradient(180deg, oklch(0.83 0.045 220) 0%, oklch(0.92 0.025 215) 55%, ${PAPER} 100%)`,
  mist: `linear-gradient(180deg, oklch(0.87 0.015 270) 0%, oklch(0.94 0.01 260) 55%, ${PAPER} 100%)`,
  night: `linear-gradient(180deg, oklch(0.58 0.11 260) 0%, oklch(0.8 0.06 250) 55%, ${PAPER} 100%)`,
};

// Night gradients — same hue families as day, dropped ~0.1 in lightness so
// the banner reads as "evening". Lightness is kept ≥ 0.55 at the darkest stop
// so the ink-colored text (L ≈ 0.22) remains legible without inversion. All
// gradients terminate at PAPER so the banner fades into the page.
const NIGHT_COND_BG: Record<string, string> = {
  sun: `linear-gradient(180deg, oklch(0.78 0.09 65) 0%, oklch(0.88 0.06 78) 55%, ${PAPER} 100%)`,
  heat: `linear-gradient(180deg, oklch(0.72 0.1 45) 0%, oklch(0.85 0.06 60) 55%, ${PAPER} 100%)`,
  cloud: `linear-gradient(180deg, oklch(0.72 0.03 240) 0%, oklch(0.86 0.02 235) 55%, ${PAPER} 100%)`,
  rain: `linear-gradient(180deg, oklch(0.66 0.055 235) 0%, oklch(0.83 0.03 225) 55%, ${PAPER} 100%)`,
  storm: `linear-gradient(180deg, oklch(0.55 0.065 255) 0%, oklch(0.78 0.04 240) 55%, ${PAPER} 100%)`,
  wind: `linear-gradient(180deg, oklch(0.76 0.035 165) 0%, oklch(0.88 0.022 155) 55%, ${PAPER} 100%)`,
  frost: `linear-gradient(180deg, oklch(0.78 0.045 205) 0%, oklch(0.9 0.025 200) 55%, ${PAPER} 100%)`,
  cold: `linear-gradient(180deg, oklch(0.7 0.05 220) 0%, oklch(0.86 0.028 215) 55%, ${PAPER} 100%)`,
  mist: `linear-gradient(180deg, oklch(0.76 0.018 270) 0%, oklch(0.88 0.012 260) 55%, ${PAPER} 100%)`,
  night: `linear-gradient(180deg, oklch(0.5 0.13 262) 0%, oklch(0.75 0.07 252) 55%, ${PAPER} 100%)`,
};

const DEFAULT_BG = COND_BG["cloud"]!;
const DEFAULT_BG_NIGHT = NIGHT_COND_BG["cloud"]!;

// Each gradient's top (most saturated) stop — its signature color. Derived once
// at module load so callers can tint small surfaces (e.g. the "today" timeline
// marker) to match the hero without re-parsing a gradient on every render.
const topStop = (gradient: string): string =>
  gradient.match(/oklch\([^)]*\)/)?.[0] ?? "var(--color-terra)";
const COND_ACCENT: Record<string, string> = Object.fromEntries(
  Object.entries(COND_BG).map(([k, v]) => [k, topStop(v)]),
);
const NIGHT_COND_ACCENT: Record<string, string> = Object.fromEntries(
  Object.entries(NIGHT_COND_BG).map(([k, v]) => [k, topStop(v)]),
);

export function useWeatherBackdrop(): {
  background: string;
  accent: string;
  dark: boolean;
  condition: WeatherCondition;
  isDay: boolean;
} {
  const { weather } = useWeather();
  const today = weather?.today;
  const currentHour = today?.hours[Temporal.Now.zonedDateTimeISO().hour];

  if (!currentHour) {
    return {
      background: DEFAULT_BG,
      accent: COND_ACCENT["cloud"]!,
      dark: false,
      condition: "cloud",
      isDay: true,
    };
  }

  const cond = currentHour.condition;
  const isDay = currentHour.isDay;
  const background = isDay
    ? (COND_BG[cond] ?? DEFAULT_BG)
    : (NIGHT_COND_BG[cond] ?? DEFAULT_BG_NIGHT);
  const accent = isDay
    ? (COND_ACCENT[cond] ?? COND_ACCENT["cloud"]!)
    : (NIGHT_COND_ACCENT[cond] ?? NIGHT_COND_ACCENT["cloud"]!);
  // Reserved for callers that want to adjust translucent surfaces (e.g. the
  // location pill's glass tint) — never used to switch foreground text color,
  // since every gradient is calibrated to keep ink-colored text legible.
  const dark = !isDay;

  return { background, accent, dark, condition: cond, isDay };
}

/**
 * The weather hero's signature color — the top (most saturated) stop of the
 * current backdrop gradient. Useful for tinting small surfaces (e.g. the
 * "today" timeline marker) so they read as "today's weather".
 */
export function useWeatherAccent(): { color: string; dark: boolean } {
  const { accent, dark } = useWeatherBackdrop();
  return { color: accent, dark };
}

// Full-screen illustrated backdrops live in WeatherBackground.tsx.
// Gradient definitions above remain for accent colors and future fallback variants.
